import { api } from './client';

export interface SearchProject {
  id: string;
  organization_id: string;
  vacancy_id: string | null;
  name: string;
  language: string;
  status: string;
  candidate_profile: {
    text?: string;
    weights?: Record<string, number>;
    grade_thresholds?: { A: number; B: number };
    outreach_template?: string;
  } | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  candidates_count?: number;
  scored_count?: number;
}

export interface SearchRun {
  id: string;
  search_project_id: string;
  bucket: string | null;
  actor_id: string;
  input_json: Record<string, unknown> | null;
  apify_run_id: string | null;
  apify_dataset_id: string | null;
  status: string;
  items_found: number;
  items_imported: number;
  items_duplicates: number;
  cost_usd: number | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
}

export interface SourcingScore {
  id: string;
  sourced_candidate_id: string;
  score_version: number;
  total_score: number | null;
  criteria: Record<
    string,
    { score: number; max: number; evidence?: string[]; assumptions?: string[]; unknowns?: string[] }
  > | null;
  required_questions: string[] | null;
  summary_ru: string | null;
  summary_en: string | null;
  red_flags: string[] | null;
  grade: string | null;
  ai_model: string | null;
  ai_cost_usd: number | null;
  created_at: string;
}

export interface SourcedCandidate {
  id: string;
  search_project_id: string;
  linkedin_url: string;
  full_name: string | null;
  headline: string | null;
  location_text: string | null;
  current_title: string | null;
  current_company: string | null;
  years_total_est: number | null;
  years_in_current_role: number | null;
  past_founder: boolean;
  fast_growth: boolean;
  speaks_russian: boolean;
  open_to_work: boolean;
  connections_count: number | null;
  bucket: string | null;
  manual_grade: string | null;
  outreach_status: string;
  outreach_channel: string | null;
  contacted_at: string | null;
  replied_at: string | null;
  promoted_candidate_id: string | null;
  created_at: string;
  total_score: number | null;
  grade: string | null;
}

export interface SourcedCandidateDetail extends SourcedCandidate {
  public_identifier: string | null;
  country: string | null;
  current_company_url: string | null;
  notes: string | null;
  raw_json: Record<string, unknown> | null;
  scores: SourcingScore[];
}

export interface SourcingPreset {
  key: string;
  bucket: string;
  name: string;
  description: string;
  actor_id: string;
  input: Record<string, unknown>;
}

export interface CandidateListResponse {
  items: SourcedCandidate[];
  total: number;
  offset: number;
  limit: number;
}

export const sourcingApi = {
  listProjects: () => api.get<SearchProject[]>('/sourcing/projects'),
  createProject: (body: Partial<SearchProject>) =>
    api.post<SearchProject>('/sourcing/projects', body),
  getProject: (id: string) => api.get<SearchProject>(`/sourcing/projects/${id}`),
  updateProject: (id: string, body: Partial<SearchProject>) =>
    api.patch<SearchProject>(`/sourcing/projects/${id}`, body),

  listRuns: (projectId: string) => api.get<SearchRun[]>(`/sourcing/projects/${projectId}/runs`),
  startRun: (projectId: string, body: { bucket?: string | null; actor_id?: string; input_json: Record<string, unknown> }) =>
    api.post<SearchRun>(`/sourcing/projects/${projectId}/runs`, body),
  getRun: (runId: string) => api.get<SearchRun>(`/sourcing/runs/${runId}`),
  importRun: (runId: string) =>
    api.post<{ found: number; imported: number; duplicates: number }>(`/sourcing/runs/${runId}/import`),
  importFile: async (projectId: string, bucket: string | null, file: File) => {
    const form = new FormData();
    form.append('project_id', projectId);
    if (bucket) form.append('bucket', bucket);
    form.append('file', file);
    const res = await api.raw('/sourcing/runs/import-file', { method: 'POST', body: form });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(typeof err?.detail === 'string' ? err.detail : `Ошибка ${res.status}`);
    }
    return res.json() as Promise<{ found: number; imported: number; duplicates: number }>;
  },

  listCandidates: (projectId: string, params: Record<string, string | number | undefined>) => {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== '')
      .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
      .join('&');
    return api.get<CandidateListResponse>(
      `/sourcing/projects/${projectId}/candidates${qs ? `?${qs}` : ''}`
    );
  },
  getCandidate: (id: string) => api.get<SourcedCandidateDetail>(`/sourcing/candidates/${id}`),
  patchCandidate: (id: string, body: Record<string, unknown>) =>
    api.patch<SourcedCandidate>(`/sourcing/candidates/${id}`, body),
  bulkContacted: (candidateIds: string[], channel = 'linkedin_message') =>
    api.post<{ updated: number }>('/sourcing/candidates/bulk-contacted', {
      candidate_ids: candidateIds,
      outreach_channel: channel,
    }),

  scoreCandidate: (id: string) => api.post<SourcingScore>(`/sourcing/candidates/${id}/score`),
  batchScore: (projectId: string) =>
    api.post<{ scored: number; failed: number; errors: unknown[] }>(
      `/sourcing/projects/${projectId}/score`
    ),

  promote: (id: string, vacancyId: string) =>
    api.post<{ candidate_id: string }>(`/sourcing/candidates/${id}/promote`, {
      vacancy_id: vacancyId,
    }),

  listPresets: () => api.get<SourcingPreset[]>('/sourcing/presets'),

  exportCsv: async (projectId: string) => {
    const res = await api.raw(`/sourcing/projects/${projectId}/export.csv`, { method: 'GET' });
    if (!res.ok) throw new Error(`Ошибка экспорта ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sourcing_export.csv';
    a.click();
    URL.revokeObjectURL(url);
  },
};
